<template>
  <div data-view="google-manager">
    <router-view />

    <layout-logged-frame title="Promotions" has-before>
      <template v-slot:header-actions>
        <Can
          action="view"
          subject="ad-manager.order"
        >
          <v-btn
            color="primary"
            @click="$router.push({ name: 'promotions-new' })"
          >
            <v-icon>add</v-icon>
            {{ $t('ad_manager_add_new_title') }}
          </v-btn>
        </Can>

        <Can
          action="view-business-contact"
          subject="ad-manager.order"
        >
          <v-btn
            id="new-promotion"
            color="primary"
            :disabled="!hasEnabledForPromotionCountries"
            @click="$router.push({ name: 'promotions-new', query: { flow: 'new-promotion' } })"
          >
            <v-icon>add</v-icon>
            {{ $t('new_promotion') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <PromotionsAlerts
          v-if="isBusiness"
          :incomplete="incompleteBilling"
          :pending="pendingBilling"
          :denied="deniedBilling"
        />

        <!-- if enabledPromoCountries is empty then the platform select will use user/countries-->
        <PromotionsFilters
          ref="ordersfilter"
          :is-skeleton-loading="isSkeletonLoading"
          :countries="enabledPromoCountries"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :columns="isBusiness ? columnsBC : columns"
          :items="orders"
          :pagination="pagination"
          :pages="totalPages"
          :is-loading="isLoading"
          :can-edit-columns="false"
          :on-select-item="onSelectItem"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
          :no-data-title="$t('no_promotions')"
          :no-data-subtitle="$t('start_to_use_our_products')"
          has-custom-items-template
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'isArchived'">
              {{ displayArchivedColumn(scope.cell.item) }}
            </template>

            <template v-else-if="scope.cell.column.value === 'contract_deal.value'">
              {{ displayContractColumn(scope.cell.item) }}
            </template>

            <template v-else-if="scope.cell.column.value === 'lines'">
              <LineItemStatus
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'impressions'">
              <LineItemImpressions
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
                :on-toggle="onToggleRow"
              />
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import PromotionsFilters from '../../components/forms/filters/Promotions';
import LineItemStatus from '../../components/panels/lineItems/Status';
import LineItemImpressions from '../../components/panels/lineItems/Impressions';
import PromotionsAlerts from '../../components/alerts/Promotions';
import columns from '../../config/promotions/orderColumns';
import columnsBC from '../../config/promotions/orderColumnsBC';
import navigationTabs from '../../config/promotions/navigationTabs';
import NavigationTabs from '../../components/NavigationTabs'
import { extractNestedProp, isStr } from '@/utils';

export default {
    components: {
      PromotionsFilters,
      LineItemStatus,
      LineItemImpressions,
      PromotionsAlerts,
      NavigationTabs
    },
    data() {
      return {
        navigationTabs,
        columns,
        columnsBC,
        totalPages: 0,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: '',
          totalItems: 0,
          search: ''
        },
        query: {},
        expandedRows: [],
        isSkeletonLoading: true,
        isLoading: false,
      };
    },
    computed: {
      disabledForPromotionCountries() {
        return this.$store.getters['businesses/activeItem']?.disabledForPromotionCountries ?? [];
      },
      hasEnabledForPromotionCountries() {
        return this.disabledForPromotionCountries.length !== this.enabledPromoCountries.length;
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      orders() {
        return this.$store.getters['promotions/items'];
      },
      billingCountriesByStatus() {
        return this.$store.getters['businesses/activeItem']?.billingCountriesByStatus ?? [];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      isBusiness() {
        return this.$ability.can('view-business-contact', 'ad-manager.order');
      },
      incompleteBilling() {
        return this.alertMessageBaseCondition('no_billing');
      },
      pendingBilling() {
        return this.alertMessageBaseCondition('networks_in_review');
      },
      deniedBilling() {
        return this.alertMessageBaseCondition('networks_denied');
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.query?.status === 'success' &&
          (from.name === 'promotion-details' || from.name === 'promotions-new') &&
          to.name === 'promotions'
        ) {
          this.getOrders(this.query);
        }
      },
    },
    created() {
      const { isBusiness, getBillingCountriesByStatus, getEnabledCountriesForPromotion } = this;

      if (isBusiness) {
        getBillingCountriesByStatus();
        getEnabledCountriesForPromotion();
      }

      this.getOrders().catch((err) => err);
    },
    methods: {
      getEnabledCountriesForPromotion() {
        return this.$store.dispatch('businessUsers/getEnabledCountriesForPromotion');
      },
      getOrders({
        page = 1,
        order = '',
        sort = '',
        search = '',
        status = -1,
        countries = [],
        businessId = -1,
        advertiserId = -1,
        date_filter = '',
        created_by = '',
        contract_name = -1,
        order_type = -1
      } = {}) {
        const query = {
          page,
          order,
          sort,
          search,
          status,
          countries,
          date_filter,
          businessId,
          advertiserId,
          created_by,
          contract_name,
          order_type
        };

        this.isLoading = true;

        return this.$store.dispatch('promotions/getOrders', query)
          .then(res => {
            const { itemsMeta } = this.$store.getters['promotions/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return err;
          });
      },
      getBillingCountriesByStatus() {
        return this.$store.dispatch('businesses/getBillingCountriesByStatus');
      },
      getFilteredOrders() {
        if (this.$refs.ordersfilter) {
          this.$refs.ordersfilter.onFilter();
        }
      },
      onFilter(filters) {
        const name = filters.name || '';
        const status = filters.status?.id || -1;
        const businessId = filters.business_id || -1;
        const advertiserId = filters.advertiser_id || -1;
        const created_by = filters.created_by || '';
        const date_filter = filters.dates || '';
        const contract_name = filters.contract_name || -1;
        const order_type = filters.order_type || -1;
        const countries = filters.countries;

        this.query = {
          ...this.query,
          ...this.getSearchQuery(filters),
          name,
          status,
          businessId,
          advertiserId,
          date_filter,
          created_by,
          page: 1,
          contract_name,
          order_type,
          countries
        };

        this.getOrders(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = {sort: sortBy};

        this.getOrders(this.query);
      },
      getSearchQuery({ search, status, dates, businessId, advertiserId, created_by }) {
        const query = {};

        query.search = search;
        query.status = status;
        query.businessId = businessId;
        query.advertiserId = advertiserId;
        query.date_filter = dates;
        query.created_by = created_by;

        return query;
      },
      onSelectItem(order) {
        this.$router.push({ name: 'promotion-details', params: { id: order.id } });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayArchivedColumn(item) {
        return item.isArchived ? 'Yes' : 'No';
      },
      displayContractColumn(item) {
        if (item?.contract_deal?.value.length) {
          return item?.contract_deal?.value;
        }
        return '-';
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy
        };
        this.query = nextQuery;

        this.getOrders(nextQuery);
      },
      onUpdatePagination({ page, order, sortBy }) {
        if (!sortBy) return null;

        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: this.pagination.sortBy,
        };
        this.query = nextQuery;

        this.getOrders(nextQuery);
      },
      onToggleRow(name) {
        const itemIndex = this.expandedRows.findIndex(i => i === name);

        if (itemIndex > -1) {
          this.expandedRows.splice(itemIndex, 1);
        } else {
          this.expandedRows.push(name);
        }
      },
      alertMessageBaseCondition(arrayName) {
        const { billingCountriesByStatus } = this;

        // eslint-disable-next-line max-len
        if ( billingCountriesByStatus && billingCountriesByStatus[arrayName] && billingCountriesByStatus[arrayName].length) {
          return billingCountriesByStatus[arrayName];
        }

        return [];
      },
    }
  };
</script>

<style lang="scss">

</style>
